/**
 * @fileoverview Person-name part codec for job contacts (MOR-643, MOR-976).
 *
 * A person job contact carries a single canonical `displayName` ("Name")
 * with three editable parts — first / middle / last — layered on top:
 *
 *   Name = Full Name = First + Middle + Last  (this IS `displayName`)
 *
 * MOR-643 originally defined Name as First + Last only, which meant moving
 * a token into Middle Name silently dropped it from the Name (and from
 * every letter that fills a contact-name placeholder) — "Cansu Damla
 * Kuvanci" collapsed to "Cansu Kuvanci". MOR-976 folds the middle name in.
 *
 * The invariant the backend enforces is
 * `displayName === First + " " + Middle + " " + Last` (empty parts skipped).
 * Editing any of Name / First / Middle / Last keeps the rest in sync:
 *   - edit a part   → recompute `displayName` from First + Middle + Last
 *   - edit the Name → re-split: when the currently-stored Middle still
 *     appears inside the typed name it stays the Middle and the tokens
 *     around it become First / Last; otherwise first word = First, the
 *     rest = Last, and the Middle is cleared (the Name field is canonical,
 *     so a middle name deleted from it must not resurrect itself)
 *
 * Backwards compatibility: existing contacts have no stored parts. We never
 * migrate them — `deriveNameParts` splits `displayName` on the fly so the
 * resolver and UI always have first/middle/last to show, and nothing is
 * written until the user actually edits a field.
 *
 * Kept dependency-free so both the Convex mutation/resolver and the React
 * detail panel can import it (mirrors `contactFields.ts`).
 */

/** The stored / derived person-name parts. `middleName` is empty when unset. */
export interface NameParts {
  firstName: string;
  middleName: string;
  lastName: string;
}

/** Collapse internal whitespace and trim. */
function clean(value: string | null | undefined): string {
  return (value ?? "").replace(/\s+/g, " ").trim();
}

/**
 * Split a display name into parts. First whitespace-delimited token is the
 * first name; everything after it is the last name; the middle name comes
 * back empty. A single-token name yields an empty last name. (MOR-643:
 * "first word = First, rest = Last" — with nothing to say whether a middle
 * token is a middle name or part of a double surname, everything after the
 * first word stays in Last. See `resplitDisplayName` for the edit path that
 * DOES know the current middle name.)
 */
export function splitDisplayName(
  displayName: string | null | undefined,
): NameParts {
  const tokens = clean(displayName).split(" ").filter(Boolean);
  if (tokens.length === 0) {
    return { firstName: "", middleName: "", lastName: "" };
  }
  const [first, ...rest] = tokens;
  return { firstName: first, middleName: "", lastName: rest.join(" ") };
}

/**
 * Re-split an edited "Name" against the currently-stored middle name
 * (MOR-976). When the middle name's tokens still appear as an interior run
 * of the typed name — not the first token, and with at least one token
 * after them — the run stays the Middle and the tokens before / after it
 * become First / Last, so retyping or fixing a name never silently drops
 * the middle. Otherwise the middle is treated as removed and the plain
 * first-word/rest split applies. In every case the parts recompose to
 * exactly the typed name, preserving the Name = First + Middle + Last
 * invariant.
 */
export function resplitDisplayName(
  displayName: string | null | undefined,
  currentMiddleName: string | null | undefined,
): NameParts {
  const tokens = clean(displayName).split(" ").filter(Boolean);
  const middleTokens = clean(currentMiddleName).split(" ").filter(Boolean);
  if (tokens.length >= 3 && middleTokens.length > 0) {
    const lastStart = tokens.length - middleTokens.length;
    for (let i = 1; i < lastStart; i++) {
      const matches = middleTokens.every(
        (t, j) => tokens[i + j].toLowerCase() === t.toLowerCase(),
      );
      if (matches) {
        return {
          firstName: tokens.slice(0, i).join(" "),
          // Keep the typed casing rather than the stored one.
          middleName: tokens.slice(i, i + middleTokens.length).join(" "),
          lastName: tokens.slice(i + middleTokens.length).join(" "),
        };
      }
    }
  }
  return splitDisplayName(displayName);
}

/** Compose the canonical "Name" (= First + Middle + Last). */
export function composeDisplayName(
  firstName: string | null | undefined,
  middleName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  return composeFullName(firstName, middleName, lastName);
}

/** Compose the "Full Name" (= First + Middle + Last). */
export function composeFullName(
  firstName: string | null | undefined,
  middleName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  return [clean(firstName), clean(middleName), clean(lastName)]
    .filter(Boolean)
    .join(" ");
}

/**
 * Resolve the effective first/middle/last for a contact. Returns the stored
 * parts when present; otherwise derives First/Last from `displayName` (with an
 * empty Middle). Use everywhere first/middle/last are read so stored and
 * legacy (unsplit) contacts behave identically.
 */
export function deriveNameParts(contact: {
  displayName: string;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}): NameParts {
  const storedFirst = clean(contact.firstName);
  const storedLast = clean(contact.lastName);
  // Treat the parts as "set" when either first or last has a value. A bare
  // middle name on its own is never enough to skip the displayName split.
  if (storedFirst || storedLast) {
    return {
      firstName: storedFirst,
      middleName: clean(contact.middleName),
      lastName: storedLast,
    };
  }
  const split = splitDisplayName(contact.displayName);
  return {
    firstName: split.firstName,
    middleName: clean(contact.middleName),
    lastName: split.lastName,
  };
}
